'use client';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Button,
  CommentItem,
  IMessage,
  IReplyNavigationInitialFocus,
  IMessagesQueryParams,
  INormalizedMessagesOfThreadResponse,
  useMessageContext,
} from '@ui/components';
import { ContextUserType } from '@ui/constants/context';
import { CommentItemSkeleton } from '../comments/comment-item-skeleton';
import { useThreadPaginationSentinel } from '@ui/hooks/use-thread-pagination-sentinel';

interface Props {
  userType: ContextUserType;
  comment: IMessage;
  creator_id: string;
  user_id: string;
  parent_comment_thread_id?: string;
  filter_by_reply_id?: string;
  target_comment_id?: string;
  target_reply_id?: string;
  initialFocus?: IReplyNavigationInitialFocus | null;
  highlight_target?: boolean;
  highlight_visible?: boolean;
  highlight_auto_scroll?: boolean;
  highlight_generation_key?: string;
  onTargetVisibilityChange?: (visible: boolean) => void;
  onTargetLayoutChange?: () => void;
  /** Anchored reply-query readiness; reply dwell must not await the comment list. */
  onTargetReadyChange?: (ready: boolean) => void;
  parent_pagination_version?: string | number;
  moderators_ids: string[];
}

export const ReplyList = ({
  userType,
  comment,
  creator_id,
  user_id,
  parent_comment_thread_id,
  filter_by_reply_id,
  target_comment_id,
  target_reply_id,
  initialFocus,
  highlight_target,
  highlight_visible,
  highlight_auto_scroll,
  highlight_generation_key,
  onTargetVisibilityChange,
  onTargetLayoutChange,
  onTargetReadyChange,
  parent_pagination_version,
  moderators_ids,
}: Props) => {
  const messageState = useMessageContext(userType);
  const focusedReplyId = target_reply_id ?? filter_by_reply_id;
  const queryIdentity = `${comment.child_thread_id ?? ''}:${
    comment.conversation_id ?? ''
  }:${creator_id}:${user_id}:${focusedReplyId ?? ''}`;
  const initializedQueryRef = useRef(queryIdentity);
  const nextPagePendingRef = useRef(false);
  const retainedTargetReplyRef = useRef<{ key: string; reply: IMessage } | null>(null);
  const initialFocusSeedRef = useRef<{ key: string; retired: boolean } | null>(null);
  const targetLiveBaselineRef = useRef<{ key: string; reply?: IMessage }>({
    key: '',
    reply: undefined,
  });
  const initialTargetResponseRef = useRef<{
    key: string;
    active: boolean;
    missing: boolean;
    acceptedAt?: number;
  } | null>(null);
  const [query, setQuery] = useState<IMessagesQueryParams>({
    ordering: 'asc',
    ...(focusedReplyId ? {} : { page: 1 }),
    pageSize: 10,
    thread_id: comment.child_thread_id,
    conversation_id: comment.conversation_id,
    creator_id,
    fan_id: user_id,
    ...(focusedReplyId && { anchor_id: focusedReplyId }),
  });

  useEffect(() => {
    if (initializedQueryRef.current === queryIdentity) return;
    initializedQueryRef.current = queryIdentity;

    setQuery((prev) => {
      const nextQuery: IMessagesQueryParams = {
        ...prev,
        pageSize: 10,
        thread_id: comment.child_thread_id,
        conversation_id: comment.conversation_id,
        creator_id,
        fan_id: user_id,
      };

      delete nextQuery.message_ids;
      if (focusedReplyId) {
        delete nextQuery.page;
        nextQuery.anchor_id = focusedReplyId;
      } else {
        delete nextQuery.anchor_id;
        nextQuery.page = 1;
      }

      return nextQuery;
    });
  }, [
    comment.child_thread_id,
    comment.conversation_id,
    creator_id,
    focusedReplyId,
    queryIdentity,
    user_id,
  ]);

  const { data, isLoading, isFetching, isSuccess, isError, error, refetch, fulfilledTimeStamp } =
    messageState.useGetMessagesOfThreadQuery(query, {
      skip: !query.thread_id || !query.creator_id || !query.conversation_id,
      selectFromResult: ({
        currentData,
        ...params
      }: {
        currentData?: INormalizedMessagesOfThreadResponse;
        data?: INormalizedMessagesOfThreadResponse;
      }) => ({
        ...params,
        data: currentData
          ? {
              ...currentData,
              messages: currentData.messageOrder
                ? (() => {
                    const orderedMessages = currentData.messageOrder
                      .map((id) => currentData.messages.entities[id])
                      .filter((message): message is IMessage => Boolean(message));
                    const orderedIds = new Set(currentData.messageOrder);
                    const newMessages = messageState.messagesSelector
                      .selectAll(currentData.messages)
                      .filter((message: IMessage) => !orderedIds.has(message.id));
                    const optimisticMessages = newMessages.filter(
                      (message: IMessage) =>
                        typeof message.tempTimeStamp === 'number' && message.tempTimeStamp > 0,
                    );
                    const serverMessages = newMessages.filter(
                      (message: IMessage) => !optimisticMessages.includes(message),
                    );
                    return [...optimisticMessages, ...orderedMessages, ...serverMessages];
                  })()
                : messageState.messagesSelector.selectAll(currentData.messages),
            }
          : undefined,
      }),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });

  const loadedReplies = useMemo(
    () =>
      [...(data?.messages ?? [])].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [data?.messages],
  );
  const communityDetails = useMemo(() => data?.community_details, [data?.community_details]);

  const currentPage = query.page ?? data?.anchorPage ?? 1;
  const isAccessError = isError && [401, 403, 404, 422].includes(Number((error as any)?.status));
  const targetReplyKey = focusedReplyId
    ? `${comment.child_thread_id}:${focusedReplyId}:${
        highlight_generation_key ?? initialFocus?.generationId ?? 'live'
      }:${initialFocus?.viewerId ?? user_id}`
    : null;
  const initialFocusReply = initialFocus?.targetType === 'reply' ? initialFocus.target : undefined;
  const initialFocusSeedKey =
    focusedReplyId && initialFocusReply
      ? `${queryIdentity}:${initialFocus?.generationId ?? ''}:${initialFocus?.viewerId ?? ''}:${
          initialFocusReply.id
        }`
      : null;
  if (initialFocusSeedRef.current?.key !== initialFocusSeedKey) {
    initialFocusSeedRef.current = initialFocusSeedKey
      ? { key: initialFocusSeedKey, retired: false }
      : null;
  }
  if (initialTargetResponseRef.current?.key !== targetReplyKey) {
    initialTargetResponseRef.current = targetReplyKey
      ? {
          key: targetReplyKey,
          active: true,
          missing: false,
          acceptedAt: initialFocus?.acceptedAt,
        }
      : null;
  }
  const loadedTargetReply = focusedReplyId
    ? loadedReplies.find((reply) => reply.id === focusedReplyId)
    : undefined;
  const targetLiveKey = `${targetReplyKey || ''}:${initialFocus?.acceptedAt ?? ''}`;
  const targetLiveChangedAfterSeed =
    targetLiveBaselineRef.current.key === targetLiveKey &&
    loadedTargetReply !== undefined &&
    targetLiveBaselineRef.current.reply !== undefined &&
    loadedTargetReply !== targetLiveBaselineRef.current.reply;
  if (targetLiveBaselineRef.current.key !== targetLiveKey) {
    targetLiveBaselineRef.current = { key: targetLiveKey, reply: loadedTargetReply };
  } else if (loadedTargetReply !== targetLiveBaselineRef.current.reply) {
    targetLiveBaselineRef.current.reply = loadedTargetReply;
  }
  const liveTargetReplyIsFresh = Boolean(
    !initialFocus?.acceptedAt ||
      targetLiveChangedAfterSeed ||
      fulfilledTimeStamp === undefined ||
      fulfilledTimeStamp >= initialFocus.acceptedAt,
  );
  const isInitialTargetRequest = Boolean(
    targetReplyKey &&
      initialTargetResponseRef.current?.active &&
      focusedReplyId &&
      query.anchor_id === focusedReplyId,
  );
  const initialTargetResponseIsFresh = Boolean(
    !initialTargetResponseRef.current?.acceptedAt ||
      fulfilledTimeStamp === undefined ||
      fulfilledTimeStamp >= initialTargetResponseRef.current.acceptedAt,
  );
  if (
    isInitialTargetRequest &&
    isSuccess &&
    !isFetching &&
    initialTargetResponseIsFresh &&
    initialTargetResponseRef.current
  ) {
    const missing = !loadedTargetReply;
    initialTargetResponseRef.current.active = false;
    initialTargetResponseRef.current.missing = missing;
    if (missing && initialFocusSeedRef.current) {
      initialFocusSeedRef.current.retired = true;
    }
  }
  const targetReply = focusedReplyId
    ? (liveTargetReplyIsFresh ? loadedTargetReply : undefined) ||
      (initialFocusSeedRef.current?.retired ? undefined : initialFocusReply)
    : undefined;

  // Reply readiness is its own lane: a reply target's dwell never awaits the
  // comment list settling, and an anchored reply query only counts as ready
  // once it has settled for this focused reply.
  useEffect(() => {
    const ready = Boolean(
      focusedReplyId &&
        isSuccess &&
        !isFetching &&
        liveTargetReplyIsFresh &&
        loadedTargetReply &&
        initialTargetResponseIsFresh,
    );
    onTargetReadyChange?.(ready);
  }, [
    initialTargetResponseIsFresh,
    focusedReplyId,
    isFetching,
    isSuccess,
    loadedTargetReply,
    liveTargetReplyIsFresh,
    onTargetReadyChange,
    query.anchor_id,
  ]);
  const initialTargetResponseMissing = Boolean(
    targetReplyKey &&
      initialTargetResponseRef.current?.key === targetReplyKey &&
      initialTargetResponseRef.current.missing,
  );
  if (!targetReplyKey || isAccessError || initialTargetResponseMissing) {
    retainedTargetReplyRef.current = null;
  } else if (targetReply) {
    retainedTargetReplyRef.current = { key: targetReplyKey, reply: targetReply };
  } else if (retainedTargetReplyRef.current?.key !== targetReplyKey) {
    retainedTargetReplyRef.current = null;
  }
  const retainedTargetReply =
    retainedTargetReplyRef.current?.key === targetReplyKey
      ? retainedTargetReplyRef.current.reply
      : undefined;
  const replies = useMemo(() => {
    if (!retainedTargetReply) {
      return loadedReplies;
    }
    if (loadedReplies.some((reply) => reply.id === retainedTargetReply.id)) {
      if (liveTargetReplyIsFresh) return loadedReplies;
      return loadedReplies.map((reply) =>
        reply.id === retainedTargetReply.id ? retainedTargetReply : reply,
      );
    }
    return [...loadedReplies, retainedTargetReply].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [liveTargetReplyIsFresh, loadedReplies, retainedTargetReply]);
  const visibleReplies = isAccessError ? [] : replies;
  const loadedMaxPage = data?.maxLoadedPage ?? data?.anchorPage ?? query.page ?? 1;
  const loadedMinPage = data?.minLoadedPage ?? data?.anchorPage ?? query.page ?? 1;
  const hasMorePages = Boolean(
    !isAccessError && isSuccess && data && loadedMaxPage < data.totalPages,
  );
  const hasEarlierPages = Boolean(query.anchor_id && loadedMinPage > 1);

  const onNextPage = useCallback(() => {
    if (isFetching || nextPagePendingRef.current || !hasMorePages) return;
    nextPagePendingRef.current = true;
    setQuery((prev) => {
      const nextQuery = { ...prev, page: loadedMaxPage + 1 };
      delete nextQuery.paging_direction;
      return nextQuery;
    });
  }, [hasMorePages, isFetching, loadedMaxPage]);

  const onPreviousPage = useCallback(() => {
    if (isFetching || nextPagePendingRef.current || !hasEarlierPages) return;
    nextPagePendingRef.current = true;
    setQuery((prev) => ({ ...prev, page: loadedMinPage - 1, paging_direction: 'older' }));
  }, [hasEarlierPages, isFetching, loadedMinPage]);

  useEffect(() => {
    if (!isFetching) nextPagePendingRef.current = false;
  }, [isFetching, query.page, query.paging_direction]);

  const paginationSentinelRef = useThreadPaginationSentinel({
    enabled: hasMorePages && !isError,
    isFetching,
    onLoadMore: onNextPage,
    resetKey: `${queryIdentity}:${currentPage}:${replies.length}`,
  });

  if (isLoading && visibleReplies.length === 0) {
    return (
      <div className="flex flex-col gap-y-6 pt-4">
        <CommentItemSkeleton />
        <CommentItemSkeleton />
      </div>
    );
  }

  if (isError && visibleReplies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-4" role="alert">
        <span className="text-sm text-korDarkGrey">Unable to load replies.</span>
        <Button type="secondary" size="small" onClick={() => void refetch()} disabled={isFetching}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div>
        {hasEarlierPages && !isFetching && !isError && (
          <div className="flex justify-center py-2">
            <Button type="secondary" size="small" onClick={onPreviousPage}>
              Load earlier replies
            </Button>
          </div>
        )}

        {visibleReplies.map((reply: IMessage) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            userType={userType}
            user_id={messageState.user_id}
            creator_id={creator_id}
            enableReply
            query={query}
            reportLabel="Report reply"
            isReply
            communityDetails={
              messageState.community_details ||
              communityDetails ||
              initialFocusReply?.community_details
            }
            isAuthenticated={messageState.isAuthenticated}
            isFanOfCreator={messageState.isFanOfCreator}
            parent_comment_thread_id={parent_comment_thread_id}
            hierarchyType="reply"
            target_comment_id={target_comment_id}
            target_reply_id={target_reply_id}
            highlight_target={highlight_target}
            highlight_visible={highlight_visible}
            highlight_auto_scroll={highlight_auto_scroll}
            highlight_generation_key={highlight_generation_key}
            onTargetVisibilityChange={onTargetVisibilityChange}
            onTargetLayoutChange={onTargetLayoutChange}
            onReplyTargetReadyChange={onTargetReadyChange}
            pagination_version={`${parent_pagination_version ?? ''}:${replies.length}`}
            moderators_ids={moderators_ids}
          />
        ))}
        {isFetching && visibleReplies.length > 0 && (
          <div className="py-4">
            <CommentItemSkeleton />
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center gap-2 py-4" role="alert">
            <span className="text-sm text-korDarkGrey">Unable to load more replies.</span>
            <Button
              type="secondary"
              size="small"
              onClick={() => void refetch()}
              disabled={isFetching}>
              Try again
            </Button>
          </div>
        )}
      </div>

      <div ref={paginationSentinelRef} aria-hidden="true" className="min-h-px" />
      {hasMorePages && !isError && !isFetching && (
        <div className="flex justify-center py-4">
          <Button type="secondary" size="small" onClick={onNextPage}>
            Load more replies
          </Button>
        </div>
      )}
    </div>
  );
};
